import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Gospel Radio — live internet streams (real audio) that keep playing
// with the screen off + lock-screen controls, with NOTHING to upload.
// Uses one persistent <audio> element at the app root. Coordinates with
// the other audio surfaces via the shared event bus so nothing overlaps.
const Ctx = createContext(null);

export function GospelRadioProvider({ children }) {
  const audioRef = useRef(null);
  const [stations, setStations] = useState([]); // context list for next/prev
  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentRef = useRef(null);
  const stationsRef = useRef([]);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { stationsRef.current = stations; }, [stations]);

  const ensureEl = useCallback(() => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "none";
      el.addEventListener("playing", () => { setIsPlaying(true); setLoading(false); });
      el.addEventListener("pause", () => setIsPlaying(false));
      el.addEventListener("waiting", () => setLoading(true));
      el.addEventListener("error", () => { setLoading(false); setIsPlaying(false); });
      audioRef.current = el;
    }
    return audioRef.current;
  }, []);

  const play = useCallback((station, list) => {
    if (!station?.url) return;
    const el = ensureEl();
    try { window.dispatchEvent(new CustomEvent("hw:audio:start")); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "gospel-radio" } })); } catch (_) {}
    if (Array.isArray(list)) setStations(list);
    setCurrent(station);
    setLoading(true);
    el.src = station.url;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => setLoading(false));
  }, [ensureEl]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !currentRef.current) return;
    if (el.paused) {
      try { window.dispatchEvent(new CustomEvent("hw:audio:start")); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "gospel-radio" } })); } catch (_) {}
      setLoading(true);
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => setLoading(false));
    } else {
      el.pause();
    }
  }, []);

  const shift = useCallback((dir) => {
    const list = stationsRef.current;
    if (!list.length) return;
    const idx = list.findIndex((s) => s.id === currentRef.current?.id);
    const ni = (((idx < 0 ? 0 : idx) + dir) % list.length + list.length) % list.length;
    play(list[ni], list);
  }, [play]);
  const next = useCallback(() => shift(1), [shift]);
  const prev = useCallback(() => shift(-1), [shift]);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (el) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) {} }
    setIsPlaying(false);
    setCurrent(null);
  }, []);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  // Pause when another audio source starts (Bible read-aloud, gospel
  // YouTube mix, Holy Words Radio) so nothing plays on top of each other.
  useEffect(() => {
    const onRead = (e) => {
      if (e?.detail?.id === "gospel-radio") return;
      const el = audioRef.current;
      if (el && !el.paused) el.pause();
    };
    const onBg = () => { const el = audioRef.current; if (el && !el.paused) el.pause(); };
    window.addEventListener("hw:read-aloud:start", onRead);
    window.addEventListener("hw:bgmusic:start", onBg);
    return () => {
      window.removeEventListener("hw:read-aloud:start", onRead);
      window.removeEventListener("hw:bgmusic:start", onBg);
    };
  }, []);

  // MediaSession — lock-screen controls that keep the stream alive.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession || !current) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: current.name || "Gospel Radio",
        artist: current.country ? `Live · ${current.country}` : "Live Radio",
        album: "Holy Words · Gospel Radio",
        artwork: current.favicon
          ? [{ src: current.favicon, sizes: "256x256" }]
          : [
              { src: "/icons/holywords-192.png", sizes: "192x192", type: "image/png" },
              { src: "/icons/holywords-512.png", sizes: "512x512", type: "image/png" },
            ],
      });
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch (_) {}
    const h = { play: () => toggle(), pause: () => toggle(), nexttrack: () => next(), previoustrack: () => prev() };
    Object.entries(h).forEach(([a, fn]) => { try { navigator.mediaSession.setActionHandler(a, fn); } catch (_) {} });
    return () => Object.keys(h).forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch (_) {} });
  }, [current, isPlaying, toggle, next, prev]);

  return (
    <Ctx.Provider value={{
      stations, current, isPlaying, loading, pickerOpen,
      play, toggle, next, prev, stop, openPicker, closePicker,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useGospelRadio() {
  return useContext(Ctx);
}
